import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/Card';
import { Target, LineChart, Lightbulb, Trophy, Users, TrendingUp } from 'lucide-react';

interface Step {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const steps: Step[] = [
  {
    icon: <Target className="w-8 h-8" />,
    title: "Choose Your Category",
    description: "Select from multiple stock categories including SPY (S&P 500), Tech, NASDAQ, Pharma, WallStreetBets, and Foreign stocks. Each category offers a unique daily stock puzzle."
  },
  {
    icon: <LineChart className="w-8 h-8" />,
    title: "Analyze the Stock Chart",
    description: "Study the daily stock chart showing price movements, patterns, and trends. Look for volatility, support/resistance levels, and overall price action to narrow down your guess. Real market data helps you learn actual stock behavior!"
  },
  {
    icon: <Lightbulb className="w-8 h-8" />,
    title: "Use Financial Metrics & Hints",
    description: "View key financial metrics like market cap, revenue, P/E ratio, and more. Need help? Use hints to reveal the industry, business model, company size, and other clues. Strategic hint usage is key to winning!"
  },
  {
    icon: <Target className="w-8 h-8" />,
    title: "Guess the Stock",
    description: "Type the company name or stock ticker symbol to make your guess. You have unlimited guesses, but fewer guesses and hints used = higher score. Can you identify the stock with minimal clues?"
  },
  {
    icon: <Trophy className="w-8 h-8" />,
    title: "Track Your Score",
    description: "Your score is based on: number of guesses, hints used, and time to solve. Complete the puzzle to unlock your shareable results and compare with friends. Build win streaks and improve your stock knowledge!"
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: "Compete on Leaderboards",
    description: "Climb global leaderboards or create private groups with friends. See how your stock guessing skills stack up against other players. Share your daily results on social media to challenge others!"
  }
];

const tips = [
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Start with Chart Analysis",
    tip: "Look at the stock chart first. Stable, slow-growing charts often indicate blue-chip companies (SPY category). Highly volatile charts might be growth stocks (Tech) or meme stocks (WallStreetBets)."
  },
  {
    icon: <Target className="w-6 h-6" />,
    title: "Use Market Cap as a Clue",
    tip: "Check the market capitalization range. Mega-cap stocks ($200B+) are usually well-known companies like Apple, Microsoft, or Amazon. Small-cap stocks are typically lesser-known companies."
  },
  {
    icon: <Lightbulb className="w-6 h-6" />,
    title: "Save Hints for Later",
    tip: "Try guessing without hints first! Each hint you use lowers your score. If you're stuck after a few guesses, then use hints strategically. The first hint is usually the most valuable."
  },
  {
    icon: <LineChart className="w-6 h-6" />,
    title: "Learn Stock Patterns",
    tip: "Play daily to learn how different types of stocks behave. Tech stocks often show high growth, financial stocks are more stable, and energy stocks can be cyclical. Pattern recognition improves with practice!"
  }
];

export default function HowToPlayPage() {
  // HowTo Schema structured data
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Play Wallstreetle Stock Guessing Game",
    "description": "Learn how to play Wallstreetle - the stock wordle and finance wordle. Step-by-step guide for this guess the stock game with ticker guessing using charts, hints, and financial metrics. Master the daily stock challenge.",
    "image": "https://wallstreetle.com/assets/share-link-image.png",
    "totalTime": "PT5M",
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description
    }))
  };

  return (
    <>
      <Helmet>
        <title>Wallstreetle | How to Play</title>
        <meta name="description" content="Learn how to play Wallstreetle - the ultimate stock wordle and finance wordle! Step-by-step guide for this guess the stock game using ticker guessing, charts, hints, and strategies. Master the daily stock challenge." />
        <meta name="keywords" content="stock wordle, finance wordle, stock guessing game, guess the stock game, ticker guessing, equity game, stock trivia, investing puzzle, daily stock challenge, how to play stock guessing game, stock puzzle game tutorial, wallstreetle how to play, stock market game guide" />
        <link rel="canonical" href="https://wallstreetle.com/how-to-play" />

        {/* Open Graph */}
        <meta property="og:title" content="Wallstreetle | How to Play" />
        <meta property="og:description" content="Complete guide for the stock wordle and finance wordle! Learn how to play this guess the stock game with ticker guessing. Master strategies, tips, and tricks for the daily stock challenge." />
        <meta property="og:url" content="https://wallstreetle.com/how-to-play" />

        {/* HowTo Schema */}
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background dark:bg-gray-900 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-4">
              How to Play Stock Wordle & Finance Wordle
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Master this guess the stock game in 6 simple steps! Learn ticker guessing using charts, hints, and financial data in our daily stock challenge while improving your stock market knowledge through this equity game and investing puzzle.
            </p>
          </div>

          {/* Steps */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-10">
              Step-by-Step Guide
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {steps.map((step, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center text-primary">
                      {step.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-bold text-primary">
                          Step {index + 1}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Pro Tips */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-10">
              Pro Tips & Strategies
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {tips.map((tip, index) => (
                <Card key={index} className="p-6 bg-gradient-to-br from-primary/5 to-transparent dark:from-primary/10">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center">
                      {tip.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">
                        {tip.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {tip.tip}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Categories Overview */}
          <Card className="p-8 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-850 mb-16">
            <h2 className="text-3xl font-bold text-center mb-6">
              Stock Categories to Master
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-center">
              {['NASDAQ 100', 'SPY (S&P 500)', 'Tech', 'Software', 'Hardware', 'Pharma', 'Financial', 'Consumer', 'Industrial', 'Energy', 'WallStreetBets', 'Foreign'].map((cat) => (
                <div key={cat} className="py-3 px-4 bg-white dark:bg-gray-700 rounded-lg font-semibold shadow-sm">
                  {cat}
                </div>
              ))}
            </div>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Card className="p-10 bg-gradient-to-br from-primary to-primary/80 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to Start Guessing Stocks?
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Put your knowledge to the test with today's daily stock puzzles!
              </p>
              <a
                href="/"
                className="inline-block px-10 py-4 bg-white text-primary rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
              >
                Play Now - It's Free!
              </a>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
